import { create } from 'zustand'
import type { DataAssetData, ContextBundle } from '@/services/api'

const MAX_TABS = 6

export type ArtifactType = 'code' | 'spec' | 'external' | 'diagram' | 'knowledge' | 'workitem' | 'topic'

export interface CodeArtifactData {
  repo: string
  path: string
  highlightLines?: [number, number]
}

export interface SpecArtifactData {
  specId: string
  sectionNumber?: number
}

export interface ExternalArtifactData {
  asset: DataAssetData
}

export interface DiagramArtifactData {
  source?: string
  label?: string
}

export interface KnowledgeArtifactData {
  nodeId: string
  title: string
}

export interface WorkItemArtifactData {
  itemId: string
  title: string
}

export interface TopicArtifactData {
  topicId?: string
  title: string
  bundle: ContextBundle
}

export type ArtifactData =
  | CodeArtifactData
  | SpecArtifactData
  | ExternalArtifactData
  | DiagramArtifactData
  | KnowledgeArtifactData
  | WorkItemArtifactData
  | TopicArtifactData

export interface ArtifactTab {
  id: string
  type: ArtifactType
  label: string
  data: ArtifactData
}

function makeTabId(type: ArtifactType, data: ArtifactData): string {
  switch (type) {
    case 'code': {
      const d = data as CodeArtifactData
      return `code:${d.repo}:${d.path}`
    }
    case 'spec': {
      const d = data as SpecArtifactData
      return `spec:${d.specId}:${d.sectionNumber ?? 'root'}`
    }
    case 'external': {
      const d = data as ExternalArtifactData
      return `ext:${d.asset.asset_id}`
    }
    case 'diagram': {
      return `diagram:${crypto.randomUUID().slice(0, 8)}`
    }
    case 'knowledge': {
      const d = data as KnowledgeArtifactData
      return `knowledge:${d.nodeId}`
    }
    case 'workitem': {
      const d = data as WorkItemArtifactData
      return `workitem:${d.itemId}`
    }
    case 'topic': {
      const d = data as TopicArtifactData
      return d.topicId ? `topic:${d.topicId}` : `topic:${crypto.randomUUID().slice(0, 8)}`
    }
    default:
      return `${type}:${crypto.randomUUID()}`
  }
}

function makeLabel(type: ArtifactType, data: ArtifactData): string {
  switch (type) {
    case 'code': {
      const d = data as CodeArtifactData
      const segments = d.path.split('/')
      return segments[segments.length - 1] || d.path
    }
    case 'spec': {
      const d = data as SpecArtifactData
      return d.sectionNumber ? `§${d.sectionNumber}` : 'Spec'
    }
    case 'external': {
      const d = data as ExternalArtifactData
      return d.asset.filename || 'External'
    }
    case 'diagram': {
      const d = data as DiagramArtifactData
      return d.label || 'Diagram'
    }
    case 'knowledge': {
      const d = data as KnowledgeArtifactData
      return d.title || 'Knowledge'
    }
    case 'workitem': {
      const d = data as WorkItemArtifactData
      return d.title || 'Work Item'
    }
    case 'topic': {
      const d = data as TopicArtifactData
      return d.title || 'Topic'
    }
    default:
      return type
  }
}

interface ArtifactState {
  tabs: ArtifactTab[]
  activeTabId: string | null
  panelOpen: boolean

  openArtifact: (type: ArtifactType, data: ArtifactData) => void
  closeTab: (tabId: string) => void
  setActiveTab: (tabId: string) => void
  updateTabData: (tabId: string, data: Partial<ArtifactData>) => void
  setPanelOpen: (open: boolean) => void
  closeAllTabs: () => void
}

export const useArtifactStore = create<ArtifactState>((set, get) => ({
  tabs: [],
  activeTabId: null,
  panelOpen: false,

  openArtifact: (type, data) => {
    const id = makeTabId(type, data)
    const { tabs } = get()

    const existing = tabs.find(t => t.id === id)
    if (existing) {
      // Tab already open — activate it and update data (e.g. new highlightLines)
      set({
        activeTabId: id,
        panelOpen: true,
        tabs: tabs.map(t => t.id === id ? { ...t, data } : t),
      })
      return
    }

    const label = makeLabel(type, data)
    const newTab: ArtifactTab = { id, type, label, data }

    let newTabs = [...tabs, newTab]
    // Enforce max tabs — remove oldest (first) tab
    while (newTabs.length > MAX_TABS) {
      newTabs.shift()
    }

    set({ tabs: newTabs, activeTabId: id, panelOpen: true })
  },

  closeTab: (tabId) => {
    const { tabs, activeTabId } = get()
    const idx = tabs.findIndex(t => t.id === tabId)
    if (idx === -1) return

    const newTabs = tabs.filter(t => t.id !== tabId)
    let newActiveId = activeTabId

    if (activeTabId === tabId) {
      // Activate the next tab, or the previous one, or null
      if (newTabs.length === 0) {
        newActiveId = null
      } else if (idx < newTabs.length) {
        newActiveId = newTabs[idx].id
      } else {
        newActiveId = newTabs[newTabs.length - 1].id
      }
    }

    set({
      tabs: newTabs,
      activeTabId: newActiveId,
      panelOpen: newTabs.length > 0,
    })
  },

  setActiveTab: (tabId) => set({ activeTabId: tabId }),

  updateTabData: (tabId, data) => {
    const { tabs } = get()
    set({
      tabs: tabs.map(t =>
        t.id === tabId ? { ...t, data: { ...t.data, ...data } } : t
      ),
    })
  },

  setPanelOpen: (open) => set({ panelOpen: open }),

  closeAllTabs: () => set({ tabs: [], activeTabId: null, panelOpen: false }),
}))
